import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewbuildingsSummary, getNewbuildingsSummarySafe } from '../../actions/newbuildingsSummary';
import { AiSummaryModalMobile, type IAiSummaryMobileActions } from '../../components/AiSummaryModalMobile';
import { selectNewbuildingsSummary } from '../../selectors/newbuildingsSummary';
import type { TThunkDispatch } from '../../types/redux/thunk';
import { NewbuildingCallToAction } from '../NewbuildingCallToAction';
import { trackShowPhoneAiSummary } from '../NewbuildingCallButton/tracking';
import { NewbuildingChatButton } from '../NewbuildingChatButton';
import {
  trackCardClickAiSummary,
  trackOpenAiModal,
  trackReportErrorAiSummary,
  trackSwitchAiSummaryTab,
} from '../../utils/AiSummaryAnalytics';
import { sendUXSEvent } from '../../utils/uxs';
import { UXFB_EVENT_AI_COMPARISON_REPORT_ERROR_MWEB } from '../../constants/uxfb';
import reload from '../../components/AiSummaryModalDesktop/assets/Reload.png';

export interface IAiSummaryModalMobileContainerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AiSummaryModalMobileContainer = ({
  open,
  onOpenChange,
}: IAiSummaryModalMobileContainerProps): React.JSX.Element => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { data: summary, requestId, status } = useSelector(selectNewbuildingsSummary);
  const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleReload = React.useCallback(() => dispatch(getNewbuildingsSummary()), [dispatch]);
  const handleTabChange = React.useCallback(
    (newbuildingId: number) => trackSwitchAiSummaryTab({ JkId: newbuildingId, siteType: 'mobile' }),
    [],
  );
  const handleReportError = React.useCallback(
    (newbuildingId: number) => {
      trackReportErrorAiSummary({ JkId: newbuildingId, siteType: 'mobile' });
      sendUXSEvent(UXFB_EVENT_AI_COMPARISON_REPORT_ERROR_MWEB);
      onOpenChange(false);
    },
    [onOpenChange],
  );
  const handleCardClick = React.useCallback(
    (newbuildingId: number) => trackCardClickAiSummary({ JkId: newbuildingId, siteType: 'mobile' }),
    [],
  );
  const newbuildingIds = React.useMemo(() => summary?.summarization.map(({ id }) => id) ?? [], [summary]);
  const hasTrackedModalOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      dispatch(getNewbuildingsSummarySafe());
    }
  }, [dispatch, open]);

  React.useEffect(() => {
    if (!open) {
      hasTrackedModalOpenRef.current = false;

      return;
    }

    if (newbuildingIds.length > 0 && open && !hasTrackedModalOpenRef.current) {
      trackOpenAiModal({
        JkIds: newbuildingIds,
        siteType: 'mobile',
      });
      hasTrackedModalOpenRef.current = true;
    }
  }, [newbuildingIds, open]);

  React.useEffect(() => {
    const image = new Image();

    image.src = reload;
  }, []);

  const actions = React.useMemo<IAiSummaryMobileActions[]>(
    () =>
      (summary?.summarization ?? []).map(newbuilding => {
        const hasChat = [...newbuilding.builders, ...newbuilding.sellers].some(contact => contact.chat?.enabled);
        const onShowPhone = () =>
          trackShowPhoneAiSummary({
            category: 'Call',
            newbuildingId: newbuilding.id,
            siteType: 'mobile',
            url: newbuilding.fullUrl,
          });

        return {
          newbuildingId: newbuilding.id,
          call: (
            <NewbuildingCallToAction
              onlyIcon
              builders={newbuilding.builders}
              newbuildingId={newbuilding.id}
              onShowPhone={onShowPhone}
              sellers={newbuilding.sellers}
              url={newbuilding.fullUrl}
            />
          ),
          onShowPhone,
          chat: hasChat ? (
            <NewbuildingChatButton
              builders={newbuilding.builders}
              fullscreen
              newbuildingId={newbuilding.id}
              sellers={newbuilding.sellers}
              size="XS"
              url={newbuilding.fullUrl}
            />
          ) : undefined,
        };
      }),
    [summary],
  );

  return (
    <AiSummaryModalMobile
      actions={actions}
      onCardClick={handleCardClick}
      onClose={handleClose}
      onReload={handleReload}
      onReportError={handleReportError}
      onTabChange={handleTabChange}
      open={open}
      requestId={requestId}
      status={status}
      summary={summary}
    />
  );
};
